import type { ReactNode } from "react";

interface TrendSparklineProps {
  data: number[];
  color?: "success" | "danger" | "muted" | "accent";
  height?: number;
  width?: number;
}

export function TrendSparkline({ data, color = "accent", height = 24, width = 64 }: TrendSparklineProps) {
  if (!data.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(" ");

  const strokeColor = {
    success: "hsl(var(--success))",
    danger:  "hsl(var(--danger))",
    muted:   "hsl(var(--muted-foreground))",
    accent:  "hsl(var(--accent))",
  }[color];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" className="flex-shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.9"
      />
    </svg>
  );
}

interface MetricCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  sub?: ReactNode;
  trend?: number[];
  trendColor?: "success" | "danger" | "muted" | "accent";
  badge?: ReactNode;
  link?: ReactNode;
  isLoading?: boolean;
  onClick?: () => void;
}

export function MetricCard({ label, value, sub, trend, trendColor, badge, isLoading, onClick }: MetricCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border bg-card px-2.5 py-1.5 xl:px-3 xl:py-2 min-w-0 overflow-hidden ${onClick ? 'cursor-pointer hover:border-accent/50 transition-colors' : ''}`}
      style={{ boxShadow: "var(--shadow-card)" }}
      onClick={onClick}
    >
      <div className="flex flex-col min-w-0 gap-0.5">
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground leading-none truncate">
          {label}
        </span>
        {isLoading ? (
          <div className="h-[22px] w-16 rounded bg-muted animate-pulse mt-0.5" />
        ) : (
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-[20px] xl:text-[22px] font-semibold text-foreground leading-none tracking-tight tabular-nums">
              {value}
            </span>
            {badge}
          </div>
        )}
        {isLoading ? (
          <div className="h-[10px] w-24 rounded bg-muted animate-pulse mt-1" />
        ) : sub ? (
          <p className="text-[10px] text-muted-foreground leading-none truncate mt-0.5">{sub}</p>
        ) : null}
      </div>
      {trend && trend.length > 1 && !isLoading && (
        <div className="flex-shrink-0 self-center">
          <TrendSparkline data={trend} color={trendColor} height={28} width={60} />
        </div>
      )}
    </div>
  );
}
